"use client";

import { useEffect } from "react";
import { GhostButton, PrimaryButton } from "@ui/buttons";
import { useTranslations } from "@i18n/provider";

type EndConversationDialogProps = {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export function EndConversationDialog({ open, onConfirm, onClose }: EndConversationDialogProps) {
  const { t } = useTranslations();

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      style={{ background: "var(--modal-backdrop)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-conversation-title"
    >
      <div
        className="surface-card w-full max-w-md animate-pop-in p-6"
        style={{ boxShadow: "var(--shadow-modal)" }}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="end-conversation-title" className="font-display text-xl font-bold text-ink">
          {t("agent.conversation.endConfirm.title")}
        </h2>
        <p className="mt-3 text-sm text-ink-muted">{t("agent.conversation.endConfirm.body")}</p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <GhostButton onClick={onClose} className="min-h-[44px]">
            {t("agent.conversation.endConfirm.cancel")}
          </GhostButton>
          <PrimaryButton onClick={onConfirm} className="min-h-[44px]">
            {t("agent.conversation.endConfirm.confirm")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
